import { ApiDimension, ApiMetric } from "@incendium/api";
import {
  Accordion,
  AccordionDetails,
  Box,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { StyledAccordianSummary } from "Components/UI/AccordianGlassCard";
import { useLocalstorageState } from "Hooks/useLocalstorageState";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import { useState } from "react";

type MetricsMode = {
  metrics: ApiMetric[];
  renderBody: (m: ApiMetric) => React.ReactNode;
  title?: ((m: ApiMetric) => string) | string;
  dimensions?: never;
  renderBodyWithDimension?: never;
  defaultClosed?: boolean;
  localStorageKey?: string;
};

type DimensionsMode = {
  dimensions: ApiDimension[];
  renderBodyWithDimension: (d: ApiDimension) => React.ReactNode;
  title?: ((d: ApiDimension) => string) | string;
  metrics?: never;
  renderBody?: never;
  defaultClosed?: boolean;
  localStorageKey?: string;
};

type StaticMode = {
  renderBody: () => React.ReactNode;
  title?: string;
  metrics?: never;
  dimensions?: never;
  renderBodyWithDimension?: never;
  defaultClosed?: boolean;
  localStorageKey?: string;
};

type IAnalyticsCardGroupProps = MetricsMode | DimensionsMode | StaticMode;

const StyledDivider = styled(Divider)({
  width: "auto",
  flexGrow: 1,
  paddingTop: 4,
});

function AnalyticsCardGroup(props: IAnalyticsCardGroupProps) {
  const { title } = props;
  const metricToName = useMetricName();
  const dimensionToName = useDimensionName();
  const [open, setOpen] = useLocalstorageState<boolean>(
    props.localStorageKey ?? "analytics-card-group-open",
    props.defaultClosed ?? true,
    !props.localStorageKey,
  );

  const isDimensionsMode =
    "dimensions" in props &&
    props.dimensions != null &&
    props.dimensions.length > 0;
  const isMetricsMode =
    "metrics" in props && props.metrics != null && props.metrics.length > 0;
  const isStaticMode = !isDimensionsMode && !isMetricsMode;

  const [metric, setMetric] = useState<ApiMetric | null>(
    isMetricsMode ? props.metrics[0] : null,
  );
  const [dimension, setDimension] = useState<ApiDimension | null>(
    isDimensionsMode ? props.dimensions[0] : null,
  );

  const selectorLabel = isDimensionsMode ? "Change dimension" : "Change Metric";
  const displayTitle = title
    ? typeof title === "function"
      ? isDimensionsMode
        ? dimension != null
          ? (title as (d: ApiDimension) => string)(dimension)
          : ""
        : metric != null
          ? (title as (m: ApiMetric) => string)(metric)
          : ""
      : title
    : null;

  const getDimensionOptionLabel = (d: ApiDimension) =>
    d === ApiDimension.DIMENSION_NOT_SET ? "All" : dimensionToName(d);

  const body = (() => {
    if (
      isDimensionsMode &&
      dimension != null &&
      props.renderBodyWithDimension
    ) {
      return props.renderBodyWithDimension(dimension);
    }
    if (isMetricsMode && metric != null && props.renderBody) {
      return props.renderBody(metric);
    }
    if (isStaticMode && props.renderBody) {
      return (props.renderBody as () => React.ReactNode)();
    }
    return null;
  })();

  return (
    <Box
      sx={{
        overflow: "hidden",
        height: "100%",
        width: "100%",
        position: "relative",
      }}
      overflow={"hidden"}
    >
      <Accordion
        expanded={open}
        onChange={(e) => setOpen(!open)}
        disableGutters
        sx={{ background: "transparent", padding: 0 }}
        elevation={0}
      >
        <StyledAccordianSummary>
          <Stack
            alignItems={"center"}
            justifyContent="space-between"
            direction="row"
            sx={{ width: "100%" }}
            spacing={4}
          >
            {displayTitle != null && displayTitle !== "" && (
              <Typography variant="subtitle1">{displayTitle}</Typography>
            )}
            <StyledDivider />
            <Stack direction="row" spacing={2} alignItems="center">
              {isDimensionsMode ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    {selectorLabel || "Change Dimension"}
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    sx={{ maxWidth: "100%", minWidth: 150, width: 200 }}
                    value={dimension ?? ""}
                    onChange={(e) => {
                      e.stopPropagation();
                      setDimension(e.target.value as ApiDimension);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {props.dimensions.map((d) => (
                      <MenuItem key={d} value={d}>
                        {getDimensionOptionLabel(d)}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              ) : isMetricsMode ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    {selectorLabel || "Change Metric"}
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    sx={{ maxWidth: "100%", minWidth: 150, width: 200 }}
                    value={metric ?? ""}
                    onChange={(e) => {
                      e.stopPropagation();
                      setMetric(e.target.value as ApiMetric);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {props.metrics.map((m) => (
                      <MenuItem key={m} value={m}>
                        {metricToName(m)}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              ) : null}
            </Stack>
          </Stack>
        </StyledAccordianSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <Box>{body}</Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default AnalyticsCardGroup;
